import { useEffect, useState, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { useStore } from '../store/useStore';
import type {
  Entitlement,
  CreateEntitlementRequest,
  UpdateEntitlementRequest,
  ProvisionNanoBundleRequest,
  EntitlementAgent,
  EntitlementLease,
  EntitlementAlarm,
  AuditLogEntry,
  AuditLogFilters,
  Product,
  CheckoutRequest,
} from '../store/entitlement-slice';
import type { Customer } from '../store/customer-slice';

const REFRESH_INTERVAL = 10000;

/// YYYY-MM-DD from a Date's LOCAL components — `toISOString()` is UTC and can
/// shift an <input type="date"> default a day for admins west of UTC.
function localDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type TabId = 'entitlements' | 'agents' | 'alarms' | 'activity';

function getUtilizationPercent(used: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((used / total) * 100);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Permanent';
  return new Date(dateStr).toLocaleDateString();
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/New_York' });
}

// ─── Sub-components ──────────────────────────────────────────────────

function TabBar({ activeTab, onTabChange }: { activeTab: TabId; onTabChange: (tab: TabId) => void }) {
  const tabs: { id: TabId; label: string; testId: string }[] = [
    { id: 'entitlements', label: 'Entitlements', testId: 'tab-entitlements' },
    { id: 'agents', label: 'Agents & Checkout', testId: 'tab-agents' },
    { id: 'alarms', label: 'Alarms', testId: 'tab-alarms' },
    { id: 'activity', label: 'Activity Log', testId: 'tab-activity' },
  ];

  return (
    <div className="flex border-b border-white/10 mb-6" role="tablist" aria-label="Entitlement tabs">
      {tabs.map((tab) => (
        <button key={tab.id} type="button" role="tab" aria-selected={activeTab === tab.id} data-testid={tab.testId}
          onClick={() => onTabChange(tab.id)}
          className={clsx('px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
            activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-text-secondary hover:text-text-primary hover:border-white/20'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function UtilizationBar({ percent }: { percent: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-dark-bg rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full transition-all duration-500',
          percent < 50 && 'bg-status-green', percent >= 50 && percent < 80 && 'bg-status-yellow', percent >= 80 && 'bg-status-red'
        )} style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
      <span className="w-10 text-right text-xs text-mono text-text-secondary">{percent}%</span>
    </div>
  );
}

function StatusBadge({ status }: { status: 'online' | 'offline' }) {
  return <span className={clsx('badge', status === 'online' ? 'badge-success' : 'badge-error')}>{status}</span>;
}

// ─── Create Entitlement Modal ────────────────────────────────────────

function CreateEntitlementModal({ isOpen, onClose, onSubmit, customers, products }: {
  isOpen: boolean; onClose: () => void; onSubmit: (request: CreateEntitlementRequest) => Promise<void>; customers: Customer[]; products: Product[];
}) {
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [productId, setProductId] = useState<number | ''>('');
  const [totalSeats, setTotalSeats] = useState(1);
  const [subStart, setSubStart] = useState(localDateString(new Date()));
  const [subEnd, setSubEnd] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form only when modal opens (not when customers/products arrays update from fetches)
  useEffect(() => {
    if (isOpen) {
      setCustomerId(customers[0]?.id ?? '');
      setProductId(products[0]?.id ?? '');
      setTotalSeats(1);
      setSubStart(localDateString(new Date()));
      setSubEnd('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (customerId === '' || productId === '') { setError('Customer and product are required'); return; }
    if (totalSeats < 1) { setError('Total seats must be at least 1'); return; }
    setIsSubmitting(true); setError(null);
    try {
      const request: CreateEntitlementRequest = { customer_id: Number(customerId), product_id: Number(productId), total_seats: totalSeats, sub_end: subEnd || null };
      if (subStart) request.sub_start = subStart;
      await onSubmit(request);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create entitlement');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card-bg border border-white/10 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Create Entitlement</h2>
          <button type="button" onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Customer *</label>
            <select value={customerId} onChange={(e) => setCustomerId(Number(e.target.value))} className="input w-full" data-testid="entitlement-customer-select">
              <option value="">Select a customer...</option>
              {customers.map((c) => (<option key={c.id} value={c.id}>{c.username} {c.display_name ? `(${c.display_name})` : ''}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Product *</label>
            <select value={productId} onChange={(e) => setProductId(Number(e.target.value))} className="input w-full" data-testid="entitlement-product-select">
              <option value="">Select a product...</option>
              {products.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">
              {products.find(p => p.id === productId)?.name?.toLowerCase().includes('fader') ? 'Total Faders' : 'Total Seats'} *
            </label>
            <input type="number" min={1} value={totalSeats} onChange={(e) => setTotalSeats(parseInt(e.target.value, 10) || 1)} className="input w-full" data-testid="entitlement-seats-input" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Subscription Start</label>
            <input type="date" value={subStart} onChange={(e) => setSubStart(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Subscription End <span className="text-dimmed">(blank = permanent)</span></label>
            <input type="date" value={subEnd} onChange={(e) => setSubEnd(e.target.value)} className="input w-full" data-testid="entitlement-sub-end-input" />
          </div>
          {error && <div className="text-sm text-red-400 bg-red-500/10 p-2 rounded border border-red-500">{error}</div>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1" data-testid="entitlement-submit-btn">{isSubmitting ? 'Creating...' : 'Create Entitlement'}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Nano Bundle Modal ───────────────────────────────────────────────
// One action provisions a Pi Nano license: the perpetual mixer seat (VML/UTL
// Nano, sub_end NULL) PLUS that seat's dated update subscription (VML Nano ->
// 17, UTL Nano -> 18; separate products because they are priced independently),
// atomically. The server picks the subscription product from the seat.

const NANO_SEAT_OPTIONS = [
  { productId: 15, label: 'VML Nano', updateLabel: 'VML Nano Update Subscription' },
  { productId: 16, label: 'UTL Nano', updateLabel: 'UtilityMix Nano Update Subscription' },
];

function NanoBundleModal({ isOpen, onClose, onSubmit, customers }: {
  isOpen: boolean; onClose: () => void; onSubmit: (request: ProvisionNanoBundleRequest) => Promise<void>; customers: Customer[];
}) {
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [seatProductId, setSeatProductId] = useState<number>(15);
  const [totalSeats, setTotalSeats] = useState(1);
  const [updateSubEnd, setUpdateSubEnd] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCustomerId(customers[0]?.id ?? '');
      setSeatProductId(15);
      setTotalSeats(1);
      // Default the update subscription to one year out.
      const d = new Date(); d.setFullYear(d.getFullYear() + 1);
      setUpdateSubEnd(localDateString(d));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // If the modal opened before customers finished loading, default the first
  // one once the list arrives — but never override a selection the user made.
  useEffect(() => {
    if (isOpen && customerId === '' && customers.length > 0) {
      setCustomerId(customers[0].id);
    }
  }, [isOpen, customers, customerId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (customerId === '') { setError('Customer is required'); return; }
    if (totalSeats < 1) { setError('Total seats must be at least 1'); return; }
    if (!updateSubEnd) { setError('Update subscription end date is required'); return; }
    setIsSubmitting(true); setError(null);
    try {
      await onSubmit({ customer_id: Number(customerId), seat_product_id: seatProductId, total_seats: totalSeats, update_sub_end: updateSubEnd });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to provision nano bundle');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const seatOption = NANO_SEAT_OPTIONS.find(o => o.productId === seatProductId);
  const seatLabel = seatOption?.label ?? 'Nano';
  const updateLabel = seatOption?.updateLabel ?? 'Nano Update Subscription';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card-bg border border-white/10 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Provision Nano Bundle</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-text-secondary hover:text-text-primary">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <p className="text-sm text-text-secondary mb-4">
          Creates a <span className="text-text-primary">permanent {seatLabel}</span> seat plus a dated <span className="text-text-primary">{updateLabel}</span> in one step. The mixer runs forever; the subscription controls update delivery for that product only.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Customer *</label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value === '' ? '' : Number(e.target.value))} className="input w-full" data-testid="nano-customer-select">
              <option value="">Select a customer...</option>
              {customers.map((c) => (<option key={c.id} value={c.id}>{c.username} {c.display_name ? `(${c.display_name})` : ''}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Product *</label>
            <select value={seatProductId} onChange={(e) => setSeatProductId(Number(e.target.value))} className="input w-full" data-testid="nano-product-select">
              {NANO_SEAT_OPTIONS.map((o) => (<option key={o.productId} value={o.productId}>{o.label}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Seats *</label>
            <input type="number" min={1} value={totalSeats} onChange={(e) => setTotalSeats(parseInt(e.target.value, 10) || 1)} className="input w-full" data-testid="nano-seats-input" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Update Subscription Ends *</label>
            <input type="date" value={updateSubEnd} onChange={(e) => setUpdateSubEnd(e.target.value)} className="input w-full" data-testid="nano-sub-end-input" />
          </div>
          {error && <div className="text-sm text-red-400 bg-red-500/10 p-2 rounded border border-red-500">{error}</div>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1" data-testid="nano-submit-btn">{isSubmitting ? 'Provisioning...' : `Provision ${seatLabel} Bundle`}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Register Agent Modal ────────────────────────────────────────────

function RegisterAgentModal({ isOpen, onClose, onSubmit }: { isOpen: boolean; onClose: () => void; onSubmit: (req: { dev_uuid: string; agent_name: string }) => Promise<void>; }) {
  const [devUuid, setDevUuid] = useState('');
  const [agentName, setAgentName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (isOpen) { setDevUuid(''); setAgentName(''); setError(null); } }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!devUuid.trim() || !agentName.trim()) { setError('Both fields are required'); return; }
    setIsSubmitting(true); setError(null);
    try { await onSubmit({ dev_uuid: devUuid.trim(), agent_name: agentName.trim() }); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to register agent'); }
    finally { setIsSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card-bg border border-white/10 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Register Agent</h2>
          <button type="button" onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Device UUID *</label>
            <input type="text" value={devUuid} onChange={(e) => setDevUuid(e.target.value)} className="input w-full" placeholder="e.g., 550e8400-e29b-41d4-a716-446655440000" data-testid="agent-uuid-input" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Agent Name *</label>
            <input type="text" value={agentName} onChange={(e) => setAgentName(e.target.value)} className="input w-full" placeholder="e.g., Studio A Console" data-testid="agent-name-input" />
          </div>
          {error && <div className="text-sm text-red-400 bg-red-500/10 p-2 rounded border border-red-500">{error}</div>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1" data-testid="agent-submit-btn">{isSubmitting ? 'Registering...' : 'Register Agent'}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Source badge: imported legacy keys vs admin-created ─────────────

function SourceBadge({ source, testId }: { source?: Entitlement['source']; testId?: string }) {
  const imported = source === 'import';
  return (
    <span className={clsx('badge w-fit', imported ? 'badge-info' : 'badge-neutral')} data-testid={testId}
      title={imported ? 'Merged from imported legacy license keys' : 'Created by an admin'}>
      {imported ? 'import' : 'admin'}
    </span>
  );
}

// ─── Entitlement Detail Panel ────────────────────────────────────────

function EntitlementDetail({ entitlement, onClose, onDelete, onUpdate }: {
  entitlement: Entitlement; onClose: () => void; onDelete: (entitlement: Entitlement) => void; onUpdate: (id: number, req: UpdateEntitlementRequest) => Promise<void>;
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editSeats, setEditSeats] = useState(entitlement.total_seats);
  const [editSubEnd, setEditSubEnd] = useState(entitlement.sub_end ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { setShowDeleteConfirm(false); setIsEditing(false); setEditSeats(entitlement.total_seats); setEditSubEnd(entitlement.sub_end ?? ''); }, [entitlement]);

  const handleSave = async () => {
    setIsSubmitting(true);
    try { await onUpdate(entitlement.id, { total_seats: editSeats, sub_end: editSubEnd || null }); setIsEditing(false); }
    catch { /* handled by store */ }
    finally { setIsSubmitting(false); }
  };

  const freeSeats = entitlement.total_seats - entitlement.used_seats;
  const utilPercent = getUtilizationPercent(entitlement.used_seats, entitlement.total_seats);
  const isFader = entitlement.product_name?.toLowerCase().includes('fader');
  const unitLabel = isFader ? 'Faders' : 'Seats';

  return (
    <div className="card p-6 animate-slide-in-right">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs text-text-secondary mb-1">Entitlement Details</p>
          <h3 className="text-lg font-semibold text-text-primary">{entitlement.product_name}</h3>
          <p className="text-sm text-dimmed">ID: {entitlement.id}</p>
        </div>
        <button type="button" onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary transition-colors" aria-label="Close">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="space-y-4">
        <div><p className="text-sm text-text-secondary mb-1">Customer</p><p className="text-sm text-text-primary">{entitlement.customer_username}</p></div>
        <div><p className="text-sm text-text-secondary mb-1">Source</p><SourceBadge source={entitlement.source} /></div>
        <div>
          <p className="text-sm text-text-secondary mb-1">{unitLabel}</p>
          {isEditing ? <input type="number" min={1} value={editSeats} onChange={(e) => setEditSeats(parseInt(e.target.value, 10) || 1)} className="input w-full" data-testid="edit-entitlement-seats-input" />
           : <p className="text-sm text-text-primary">{entitlement.used_seats} / {entitlement.total_seats} used ({freeSeats} free)</p>}
        </div>
        <div><p className="text-sm text-text-secondary mb-1">Utilization</p><UtilizationBar percent={utilPercent} /></div>
        <div><p className="text-sm text-text-secondary mb-1">Subscription Start</p><p className="text-sm text-text-primary">{formatDate(entitlement.sub_start)}</p></div>
        <div>
          <p className="text-sm text-text-secondary mb-1">Subscription End</p>
          {isEditing ? <input type="date" value={editSubEnd} onChange={(e) => setEditSubEnd(e.target.value)} className="input w-full" />
           : <p className="text-sm text-text-primary">{formatDate(entitlement.sub_end)}</p>}
        </div>
        <div><p className="text-sm text-text-secondary mb-1">Created</p><p className="text-sm text-text-primary">{formatDateTime(entitlement.created_at)}</p></div>

        <div className="flex gap-2 pt-4 border-t border-white/10">
          {isEditing ? (
            <>
              <button onClick={handleSave} disabled={isSubmitting} className="btn btn-primary flex-1">{isSubmitting ? 'Saving...' : 'Save'}</button>
              <button onClick={() => { setIsEditing(false); setEditSeats(entitlement.total_seats); setEditSubEnd(entitlement.sub_end ?? ''); }} className="btn btn-secondary">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setIsEditing(true)} className="btn btn-primary flex-1" data-testid="edit-entitlement-btn">Edit</button>
              {!showDeleteConfirm ? (
                <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-danger" data-testid="delete-entitlement-btn">Delete</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => onDelete(entitlement)} className="btn btn-danger" data-testid="confirm-delete-btn">Confirm</button>
                  <button onClick={() => setShowDeleteConfirm(false)} className="btn btn-secondary">Cancel</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab 1: Entitlements ─────────────────────────────────────────────

function EntitlementsTab({ entitlements, selectedEntitlement, onSelect, onDelete, onUpdate, onCreateClick, onNanoBundleClick, isLoading }: {
  entitlements: Entitlement[]; selectedEntitlement: Entitlement | null; onSelect: (e: Entitlement | null) => void;
  onDelete: (e: Entitlement) => void; onUpdate: (id: number, req: UpdateEntitlementRequest) => Promise<void>; onCreateClick: () => void; onNanoBundleClick: () => void; isLoading: boolean;
}) {
  return (
    <div role="tabpanel" id="panel-entitlements" aria-labelledby="tab-entitlements">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-text-primary">Entitlements</h2>
        <div className="flex gap-2">
          <button onClick={onNanoBundleClick} className="btn btn-secondary" data-testid="nano-bundle-btn">+ Nano Bundle</button>
          <button onClick={onCreateClick} className="btn btn-primary" data-testid="create-entitlement-btn">+ Create Entitlement</button>
        </div>
      </div>
      <div className="flex gap-6 min-h-0">
        <div className="flex-1 card overflow-hidden flex flex-col">
          <div className="grid grid-cols-[1fr_1fr_80px_80px_80px_80px_160px_200px] gap-3 items-center p-3 bg-dark-bg border-b border-white/10 text-xs font-medium text-text-secondary uppercase tracking-wider">
            <span>Customer</span><span>Product</span><span>Source</span><span>Total</span><span>Used</span><span>Free</span><span>Sub Period</span><span>Utilization</span>
          </div>
          <div className="overflow-auto thin-scrollbar max-h-[calc(100vh-340px)]">
            {isLoading && entitlements.length === 0 ? (
              <div className="flex items-center justify-center py-12"><svg className="h-8 w-8 animate-spin text-primary" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg></div>
            ) : entitlements.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12"><p className="text-text-secondary">No entitlements found</p><button onClick={onCreateClick} className="btn btn-primary mt-4">Create Entitlement</button></div>
            ) : (
              entitlements.map((ent) => {
                const freeSeats = ent.total_seats - ent.used_seats;
                const utilPercent = getUtilizationPercent(ent.used_seats, ent.total_seats);
                const isSelected = selectedEntitlement?.id === ent.id;
                return (
                  <div key={ent.id} role="button" tabIndex={0} onClick={() => onSelect(isSelected ? null : ent)} onKeyDown={(e) => e.key === 'Enter' && onSelect(isSelected ? null : ent)}
                    className={clsx('grid grid-cols-[1fr_1fr_80px_80px_80px_80px_160px_200px] gap-3 items-center p-3 border-b border-white/5 cursor-pointer transition-colors', isSelected ? 'bg-primary/10' : 'hover:bg-light-bg')}
                    data-testid={`entitlement-row-${ent.id}`}
                  >
                    <span className="text-sm text-text-primary truncate">{ent.customer_username}</span>
                    <span className="text-sm text-text-primary truncate">{ent.product_name}</span>
                    <SourceBadge source={ent.source} testId={`entitlement-source-${ent.id}`} />
                    <span className="text-sm text-text-primary">{ent.total_seats}</span>
                    <span className="text-sm text-text-primary">{ent.used_seats}</span>
                    <span className={clsx('text-sm', freeSeats === 0 ? 'text-status-red font-medium' : 'text-text-primary')}>{freeSeats}</span>
                    <span className="text-xs text-text-secondary">{formatDate(ent.sub_start)} - {formatDate(ent.sub_end)}</span>
                    <UtilizationBar percent={utilPercent} />
                  </div>
                );
              })
            )}
          </div>
        </div>
        {selectedEntitlement && (
          <div className="w-96 shrink-0"><EntitlementDetail entitlement={selectedEntitlement} onClose={() => onSelect(null)} onDelete={onDelete} onUpdate={onUpdate} /></div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 2: Agents & Checkout ────────────────────────────────────────

type AgentSortKey = 'agent_name' | 'customer_username' | 'last_ip' | 'last_seen_at' | 'status' | 'active_leases';
type SortDir = 'asc' | 'desc';

function SortHeader({ label, sortKey, currentSort, currentDir, onSort }: {
  label: string; sortKey: AgentSortKey; currentSort: AgentSortKey; currentDir: SortDir; onSort: (key: AgentSortKey) => void;
}) {
  const isActive = currentSort === sortKey;
  return (
    <button type="button" onClick={() => onSort(sortKey)}
      className={clsx('flex items-center gap-1 text-xs font-medium uppercase tracking-wider transition-colors',
        isActive ? 'text-primary' : 'text-text-secondary hover:text-text-primary'
      )}
    >
      {label}
      {isActive && <span className="text-[10px]">{currentDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
    </button>
  );
}

function AgentsTab({ agents, products, leases, onRegisterClick, onCheckout, onRelease, onReleaseAll }: {
  agents: EntitlementAgent[]; products: Product[]; leases: EntitlementLease[]; onRegisterClick: () => void;
  onCheckout: (req: CheckoutRequest, ip?: string) => Promise<unknown>; onRelease: (leaseId: number) => Promise<void>; onReleaseAll: (agentId: number) => Promise<void>;
}) {
  const [checkoutAgentId, setCheckoutAgentId] = useState<number | ''>('');
  const [checkoutProductId, setCheckoutProductId] = useState<number | ''>('');
  const [checkoutIp, setCheckoutIp] = useState('');
  const [checkoutResult, setCheckoutResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [customerFilter, setCustomerFilter] = useState<number | ''>('');
  const [sortKey, setSortKey] = useState<AgentSortKey>('customer_username');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: AgentSortKey) => {
    if (sortKey === key) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filteredAndSortedAgents = useMemo(() => {
    let list = agents;
    if (customerFilter !== '') {
      list = list.filter((a) => a.customer_id === customerFilter);
    }
    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'agent_name': cmp = a.agent_name.localeCompare(b.agent_name); break;
        case 'customer_username': cmp = (a.customer_username ?? '').localeCompare(b.customer_username ?? ''); break;
        case 'last_ip': cmp = a.last_ip.localeCompare(b.last_ip); break;
        case 'last_seen_at': cmp = new Date(a.last_seen_at).getTime() - new Date(b.last_seen_at).getTime(); break;
        case 'status': cmp = a.status.localeCompare(b.status); break;
        case 'active_leases': cmp = a.active_leases - b.active_leases; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [agents, customerFilter, sortKey, sortDir]);

  const agentLeaseMap = useMemo(() => {
    const map = new Map<number, EntitlementLease[]>();
    leases.forEach((l) => { const existing = map.get(l.agent_id) || []; existing.push(l); map.set(l.agent_id, existing); });
    return map;
  }, [leases]);

  // Build unique customer list from agents for the filter dropdown
  const agentCustomers = useMemo(() => {
    const seen = new Map<number, string>();
    agents.forEach((a) => { if (!seen.has(a.customer_id)) seen.set(a.customer_id, a.customer_username); });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [agents]);

  const handleCheckout = async () => {
    if (checkoutAgentId === '' || checkoutProductId === '') { setCheckoutResult({ type: 'error', message: 'Agent and product are required' }); return; }
    setIsCheckingOut(true); setCheckoutResult(null);
    try {
      const result = await onCheckout({ product_id: Number(checkoutProductId), agent_id: Number(checkoutAgentId) }, checkoutIp || undefined);
      const resultObj = result as Record<string, unknown>;
      const status = (resultObj.status as string) ?? 'granted';
      setCheckoutResult({ type: 'success', message: `Checkout ${status}. Lease ID: ${resultObj.lease_id ?? 'N/A'}` });
    } catch (err) {
      setCheckoutResult({ type: 'error', message: err instanceof Error ? err.message : 'Checkout failed' });
    } finally { setIsCheckingOut(false); }
  };

  return (
    <div role="tabpanel" id="panel-agents" aria-labelledby="tab-agents" className="space-y-8">
      {/* Agents */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Agents</h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-text-secondary">Customer:</label>
              <select value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value ? Number(e.target.value) : '')} className="input w-48" data-testid="agent-customer-filter">
                <option value="">All Customers</option>
                {agentCustomers.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
            <button onClick={onRegisterClick} className="btn btn-primary" data-testid="register-agent-btn">+ Register Agent</button>
          </div>
        </div>
        <div className="card overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_130px_160px_80px_100px] gap-3 items-center p-3 bg-dark-bg border-b border-white/10">
            <SortHeader label="Agent Name" sortKey="agent_name" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
            <SortHeader label="Customer" sortKey="customer_username" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
            <SortHeader label="Last IP" sortKey="last_ip" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
            <SortHeader label="Last Seen" sortKey="last_seen_at" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
            <SortHeader label="Status" sortKey="status" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
            <SortHeader label="Leases" sortKey="active_leases" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
          </div>
          <div className="overflow-auto thin-scrollbar max-h-64">
            {filteredAndSortedAgents.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-text-secondary text-sm">
                {customerFilter !== '' ? 'No agents for this customer' : 'No agents registered'}
              </div>
            ) : filteredAndSortedAgents.map((agent) => (
              <div key={agent.id} className="grid grid-cols-[1fr_1fr_130px_160px_80px_100px] gap-3 items-center p-3 border-b border-white/5 hover:bg-light-bg/50 transition-colors" data-testid={`agent-row-${agent.id}`}>
                <span className="text-sm text-text-primary truncate">{agent.agent_name}</span>
                <span className="text-sm text-text-primary truncate">{agent.customer_username}</span>
                <span className="text-sm text-mono text-text-secondary">{agent.last_ip}</span>
                <span className="text-xs text-text-secondary">{formatDateTime(agent.last_seen_at)}</span>
                <StatusBadge status={agent.status} />
                <span className="text-sm text-text-primary">{agent.active_leases}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Checkout */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-4">License Checkout</h2>
        <div className="card p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Agent</label>
              <select value={checkoutAgentId} onChange={(e) => setCheckoutAgentId(e.target.value ? Number(e.target.value) : '')} className="input w-full" data-testid="checkout-agent-select">
                <option value="">Select agent...</option>
                {agents.map((a) => (<option key={a.id} value={a.id}>{a.agent_name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Product</label>
              <select value={checkoutProductId} onChange={(e) => setCheckoutProductId(e.target.value ? Number(e.target.value) : '')} className="input w-full" data-testid="checkout-product-select">
                <option value="">Select product...</option>
                {products.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">IP Address <span className="text-dimmed">(optional)</span></label>
              <input type="text" value={checkoutIp} onChange={(e) => setCheckoutIp(e.target.value)} className="input w-full" placeholder="e.g., 192.168.1.100" data-testid="checkout-ip-input" />
            </div>
            <button onClick={handleCheckout} disabled={isCheckingOut} className="btn btn-primary" data-testid="checkout-btn">{isCheckingOut ? 'Checking out...' : 'Checkout'}</button>
          </div>
          {checkoutResult && (
            <div className={clsx('mt-4 p-3 rounded border text-sm',
              checkoutResult.type === 'success' ? 'bg-status-green/10 border-status-green/30 text-status-green' : 'bg-status-red/10 border-status-red/30 text-status-red'
            )} data-testid="checkout-result">{checkoutResult.message}</div>
          )}
        </div>
      </div>

      {/* Leases */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-4">Active Leases</h2>
        <div className="card overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_130px_80px_160px_160px_100px] gap-3 items-center p-3 bg-dark-bg border-b border-white/10 text-xs font-medium text-text-secondary uppercase tracking-wider">
            <span>Product</span><span>Agent</span><span>IP</span><span>Seats</span><span>Granted At</span><span>Expires At</span><span>Actions</span>
          </div>
          <div className="overflow-auto thin-scrollbar max-h-[calc(100vh-500px)]">
            {leases.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-text-secondary text-sm">No active leases</div>
            ) : leases.map((lease) => (
              <div key={lease.lease_id} className="grid grid-cols-[1fr_1fr_130px_80px_160px_160px_100px] gap-3 items-center p-3 border-b border-white/5 hover:bg-light-bg/50 transition-colors" data-testid={`lease-row-${lease.lease_id}`}>
                <span className="text-sm text-text-primary truncate">{lease.product_name ?? `Product #${lease.product_id}`}</span>
                <span className="text-sm text-text-primary truncate">{lease.agent_name}</span>
                <span className="text-sm text-mono text-text-secondary">{lease.agent_ip}</span>
                <span className="text-sm text-text-primary">{lease.seats}</span>
                <span className="text-xs text-text-secondary">{formatDateTime(lease.granted_at)}</span>
                <span className="text-xs text-text-secondary">{formatDateTime(lease.expires_at)}</span>
                <button onClick={() => onRelease(lease.lease_id)} className="btn btn-danger text-xs px-2 py-1" data-testid={`release-lease-btn-${lease.lease_id}`}>Release</button>
              </div>
            ))}
          </div>
          {agentLeaseMap.size > 0 && (
            <div className="border-t border-white/10 p-3 flex flex-wrap gap-2">
              {Array.from(agentLeaseMap.entries()).map(([agentId, agentLeases]) => {
                const agentName = agentLeases[0]?.agent_name ?? `Agent #${agentId}`;
                return (
                  <button key={agentId} onClick={() => onReleaseAll(agentId)} className="btn btn-secondary text-xs" data-testid={`release-all-btn-${agentId}`}>
                    Release All ({agentName})
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab 3: Alarms ──────────────────────────────────────────────────

function AlarmsTab({ alarms, onCleanup, onClear }: {
  alarms: EntitlementAlarm[];
  onCleanup: () => Promise<void>;
  onClear: (alarmId: number) => Promise<void>;
}) {
  const [isRunning, setIsRunning] = useState(false);
  const [clearingId, setClearingId] = useState<number | null>(null);
  const [clearError, setClearError] = useState<string | null>(null);
  const activeAlarms = useMemo(() => alarms.filter((a) => a.is_active), [alarms]);

  const handleCleanup = async () => {
    setIsRunning(true);
    try { await onCleanup(); } catch { /* handled upstream */ } finally { setIsRunning(false); }
  };

  const handleClear = async (alarmId: number) => {
    setClearingId(alarmId);
    setClearError(null);
    try {
      await onClear(alarmId);
    } catch (err) {
      // e.g. 503 "lock store unreachable": the row stays, tell the admin why.
      setClearError(err instanceof Error ? err.message : 'Failed to clear alarm');
    } finally {
      setClearingId(null);
    }
  };

  /** Human-readable summary for a LOGIN_LOCKOUT alarm (see lib/lockout-alarm.ts). */
  function describeLockout(details: Record<string, unknown>): string {
    const user = String(details.username ?? '?');
    const ip = String(details.source_ip ?? '?');
    const limit = Number(details.failure_limit ?? 0);
    const windowMin = Math.round(Number(details.window_seconds ?? 0) / 60);
    const lockMin = Math.round(Number(details.lockout_seconds ?? 0) / 60);
    const trips = Number(details.trip_count ?? 1);
    const scope = details.scope === 'ip'
      ? `all logins from ${ip}`
      : `account "${user}" from ${ip}`;
    return `Locked ${scope} for ${lockMin} min after ${limit} failed logins in ${windowMin} min`
      + ` (last attempt used "${user}")${trips > 1 ? ` — tripped ${trips}×` : ''}.`
      + ' Whatever logs in from that address with this account (a license server, a dashboard user, or a Fleet alt-credentials lookup) is presenting the wrong password; an address-wide lock may also be credential stuffing across usernames. Clear to lift the lock now.';
  }

  /** Human-readable summary for LICENSE_SITE_MOVE / SEAT_HOP (see lib/site-move.ts). */
  function describeSiteMove(alarmType: string, details: Record<string, unknown>): string {
    const from = String(details.from_site ?? details.from_ip ?? '?');
    const to = String(details.to_site ?? details.to_ip ?? '?');
    const trips = Number(details.trip_count ?? 1);
    const repeat = trips > 1 ? ` Seen ${trips}×; last at ${formatDateTime(String(details.last_at ?? ''))}.` : '';
    if (alarmType === 'SEAT_HOP') {
      const fromAgent = details.from_agent_name ? `${details.from_agent_name} #${details.from_agent_id}` : `agent #${details.from_agent_id}`;
      const toAgent = details.agent_name ? `${details.agent_name} #${details.agent_id}` : `agent #${details.agent_id}`;
      return `${details.seats} seat(s) freed by ${fromAgent} at ${from} were picked up by ${toAgent} at ${to} within the hop window; the grant needed them. One license is being time-shared between sites.${repeat}`;
    }
    const agent = details.agent_name ? `${details.agent_name} #${details.agent_id}` : `agent #${details.agent_id}`;
    const how = details.via === 'renew' || details.via === 'validate' ? 'renewed its live lease' : 'checked out again after a release or lapse';
    const uuid = details.dev_uuid ? ` (dev_uuid ${String(details.dev_uuid).slice(0, 12)}…)` : '';
    return `License server ${agent}${uuid} held this license at ${from}, then ${how} from ${to}. Relocated or cloned license server.${repeat}`;
  }

  function getAlarmBgColor(alarmType: string): string {
    if (alarmType === 'DUPLICATE_IP' || alarmType === 'OVER_SEAT' || alarmType === 'LICENSE_SITE_MOVE' || alarmType === 'SEAT_HOP') return 'bg-status-red/10 border-l-4 border-l-status-red';
    return 'bg-status-yellow/10 border-l-4 border-l-status-yellow';
  }

  function getAlarmBadgeClass(alarmType: string): string {
    if (alarmType === 'DUPLICATE_IP' || alarmType === 'OVER_SEAT' || alarmType === 'LICENSE_SITE_MOVE' || alarmType === 'SEAT_HOP') return 'badge-error';
    return 'badge-warning';
  }

  return (
    <div role="tabpanel" id="panel-alarms" aria-labelledby="tab-alarms">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-text-primary">Alarms</h2>
        <button onClick={handleCleanup} disabled={isRunning} className="btn btn-secondary" data-testid="run-cleanup-btn">{isRunning ? 'Running...' : 'Run Cleanup'}</button>
      </div>
      {clearError && <div className="mb-4 text-sm text-red-400 bg-red-500/10 p-2 rounded border border-red-500" data-testid="clear-alarm-error">{clearError}</div>}
      {activeAlarms.length === 0 ? (
        <div className="card p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-dimmed" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <h3 className="mt-4 text-lg font-medium text-text-primary">No active alarms</h3>
          <p className="mt-2 text-text-secondary">All clear. No issues detected.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="grid grid-cols-[130px_1fr_1fr_2fr_150px_80px] gap-3 items-center p-3 bg-dark-bg border-b border-white/10 text-xs font-medium text-text-secondary uppercase tracking-wider">
            <span>Type</span><span>Customer</span><span>Product</span><span>Details</span><span>Created At</span><span></span>
          </div>
          <div className="overflow-auto thin-scrollbar max-h-[calc(100vh-340px)]">
            {activeAlarms.map((alarm) => (
              <div key={alarm.id} className={clsx('grid grid-cols-[130px_1fr_1fr_2fr_150px_80px] gap-3 items-center p-3 border-b border-white/5', getAlarmBgColor(alarm.alarm_type))} data-testid={`alarm-row-${alarm.id}`}>
                <span className={clsx('badge', getAlarmBadgeClass(alarm.alarm_type))}>{alarm.alarm_type}</span>
                <span className="text-sm text-text-primary truncate">{alarm.customer_username ?? (alarm.alarm_type === 'LOGIN_LOCKOUT' ? String(alarm.details?.username ?? '') : '')}</span>
                <span className="text-sm text-text-primary truncate">{alarm.product_name}</span>
                <div className="text-xs text-text-secondary">
                  {alarm.alarm_type === 'DUPLICATE_IP' && alarm.details?.ips ? (
                    <span>IPs: {(alarm.details.ips as string[]).join(', ')}</span>
                  ) : alarm.alarm_type === 'LOGIN_LOCKOUT' ? (
                    <span data-testid={`alarm-lockout-${alarm.id}`}>{describeLockout(alarm.details ?? {})}</span>
                  ) : alarm.alarm_type === 'LICENSE_SITE_MOVE' || alarm.alarm_type === 'SEAT_HOP' ? (
                    <span data-testid={`alarm-sitemove-${alarm.id}`}>{describeSiteMove(alarm.alarm_type, alarm.details ?? {})}</span>
                  ) : (
                    <pre className="text-xs text-mono overflow-x-auto whitespace-pre-wrap">{JSON.stringify(alarm.details, null, 1)}</pre>
                  )}
                </div>
                <span className="text-xs text-text-secondary">{formatDateTime(alarm.created_at)}</span>
                <button
                  onClick={() => handleClear(alarm.id)}
                  disabled={clearingId === alarm.id}
                  className="btn btn-secondary"
                  title={alarm.alarm_type === 'LOGIN_LOCKOUT' ? 'Clear the alarm and lift the login lock' : 'Clear the alarm'}
                  data-testid={`clear-alarm-${alarm.id}`}
                >
                  {clearingId === alarm.id ? '...' : 'Clear'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 4: Activity Log ────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  checkout: 'License Checkout',
  checkout_renew: 'License Renewal',
  checkout_release: 'Seats Released (zero usage)',
  validate: 'Lease Validated',
  release: 'Lease Released',
  release_all: 'Release All',
  login: 'Login',
  login_failed: 'Login Failed',
  logout: 'Logout',
  register_agent: 'Agent Registered',
  register_server: 'Server Registered',
  deregister_server: 'Server Deregistered',
  create_server_invitation: 'Server Invitation Created',
  create_customer: 'Customer Created',
  update_customer: 'Customer Updated',
  delete_customer: 'Customer Deleted',
  create_entitlement: 'Entitlement Created',
  update_entitlement: 'Entitlement Updated',
  delete_entitlement: 'Entitlement Deleted',
  provision_nano_bundle: 'Nano Bundle Provisioned',
  license_import: 'License Imported',
  license_import_conflict: 'License Import Conflict',
  clear_alarm: 'Alarm Cleared',
  clear_alarm_failed: 'Alarm Clear Failed',
};

const FAILURE_REASON_LABELS: Record<string, string> = {
  bad_password: 'wrong password',
  unknown_user: 'unknown username',
  account_disabled: 'account disabled',
};

/// Action dropdown entries: every known action plus any unlabeled action that
/// shows up in the loaded rows (so a new backend action is still filterable),
/// plus the one currently selected so an empty result cannot drop it.
function actionOptions(logs: AuditLogEntry[], selected?: string): Array<[string, string]> {
  const seen = new Map<string, string>(Object.entries(ACTION_LABELS));
  logs.forEach((l) => { if (!seen.has(l.action)) seen.set(l.action, l.action); });
  if (selected && !seen.has(selected)) seen.set(selected, selected);
  return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
}

function getActionBadgeClass(action: string): string {
  if (action === 'checkout' || action === 'checkout_renew') return 'badge-success';
  if (action === 'release' || action === 'release_all' || action === 'checkout_release') return 'badge-error';
  if (action === 'login_failed' || action === 'clear_alarm_failed' || action === 'license_import_conflict') return 'badge-error';
  if (action === 'validate') return 'badge-info';
  if (action === 'login' || action === 'register_agent') return 'badge-warning';
  return 'badge-info';
}

/// Every license server registers under the same default name, so an agent
/// is only identifiable together with its customer. Registration may omit the
/// name, and older list endpoints omit the customer: fall back rather than
/// blank the whole tab.
function agentLabel(a: EntitlementAgent): string {
  return `${a.customer_username ?? `customer #${a.customer_id}`} \u00b7 ${a.agent_name ?? 'unnamed'} (${(a.dev_uuid ?? '').substring(0, 8)}\u2026)`;
}

function ActivityLogTab({ logs, agents, customers, filters, onFilterChange }: {
  logs: AuditLogEntry[]; agents: EntitlementAgent[]; customers: Customer[];
  filters: AuditLogFilters; onFilterChange: (filters: AuditLogFilters) => void;
}) {
  const actions = useMemo(() => actionOptions(logs, filters.action), [logs, filters.action]);
  const sortedCustomers = useMemo(() => [...customers].sort((a, b) => a.username.localeCompare(b.username)), [customers]);
  const sortedAgents = useMemo(() => [...agents].sort((a, b) => agentLabel(a).localeCompare(agentLabel(b))), [agents]);
  const hasFilter = filters.agentId !== undefined || filters.customerId !== undefined || !!filters.action;

  return (
    <div role="tabpanel" id="panel-activity" aria-labelledby="tab-activity">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <h2 className="text-lg font-semibold text-text-primary">Activity Log</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm text-text-secondary" htmlFor="activity-customer-filter">Customer:</label>
          <select
            id="activity-customer-filter"
            value={filters.customerId ?? ''}
            onChange={(e) => onFilterChange({ ...filters, customerId: e.target.value ? Number(e.target.value) : undefined })}
            className="input w-44"
            data-testid="activity-customer-filter"
          >
            <option value="">All Customers</option>
            {sortedCustomers.map((c) => (
              <option key={c.id} value={c.id}>{c.username}</option>
            ))}
          </select>
          <label className="text-sm text-text-secondary" htmlFor="activity-action-filter">Activity:</label>
          <select
            id="activity-action-filter"
            value={filters.action ?? ''}
            onChange={(e) => onFilterChange({ ...filters, action: e.target.value || undefined })}
            className="input w-52"
            data-testid="activity-action-filter"
          >
            <option value="">All Activity</option>
            {actions.map(([action, label]) => (
              <option key={action} value={action}>{label}</option>
            ))}
          </select>
          <label className="text-sm text-text-secondary" htmlFor="activity-agent-filter">Agent:</label>
          <select
            id="activity-agent-filter"
            value={filters.agentId ?? ''}
            onChange={(e) => onFilterChange({ ...filters, agentId: e.target.value ? Number(e.target.value) : undefined })}
            className="input w-64"
            data-testid="activity-agent-filter"
          >
            <option value="">All Agents</option>
            {sortedAgents.map((a) => (
              <option key={a.id} value={a.id}>{agentLabel(a)}</option>
            ))}
          </select>
          {hasFilter && (
            <button type="button" onClick={() => onFilterChange({})} className="btn btn-secondary text-xs" data-testid="activity-clear-filters">
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="card overflow-hidden">
        <div className="grid grid-cols-[160px_150px_1fr_1fr_130px] gap-3 items-center p-3 bg-dark-bg border-b border-white/10 text-xs font-medium text-text-secondary uppercase tracking-wider">
          <span>Time</span><span>Action</span><span>Actor</span><span>Details</span><span>IP Address</span>
        </div>
        <div className="overflow-auto thin-scrollbar max-h-[calc(100vh-340px)]">
          {logs.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-text-secondary text-sm">
              {hasFilter ? 'No activity matches these filters' : 'No activity recorded'}
            </div>
          ) : logs.map((log) => {
            const details = log.details ?? {};
            const detailParts: string[] = [];
            if (details.reason) detailParts.push(FAILURE_REASON_LABELS[details.reason] ?? details.reason);
            if (details.locked_out) detailParts.push('lockout tripped');
            if (details.via === 'alt_credentials') detailParts.push('via alt credentials');
            if (details.product_id) detailParts.push(`Product #${details.product_id}`);
            if (details.agent_id) detailParts.push(`Agent #${details.agent_id}`);
            if (details.seats !== undefined) detailParts.push(`${details.seats} seat${details.seats !== 1 ? 's' : ''}`);
            if (details.released_count) detailParts.push(`${details.released_count} released`);
            if (details.dev_uuid) detailParts.push(details.dev_uuid.substring(0, 12) + '...');
            if (details.agent_name) detailParts.push(details.agent_name);
            if (log.resource_type && log.resource_id) detailParts.push(`${log.resource_type} #${log.resource_id}`);

            return (
              <div key={log.id} className="grid grid-cols-[160px_150px_1fr_1fr_130px] gap-3 items-center p-3 border-b border-white/5 hover:bg-light-bg/50 transition-colors" data-testid={`activity-row-${log.id}`}>
                <span className="text-xs text-text-secondary">{formatDateTime(log.created_at)}</span>
                <span className={clsx('badge text-xs', getActionBadgeClass(log.action))} data-testid={`activity-badge-${log.id}`}>{ACTION_LABELS[log.action] ?? log.action}</span>
                <span className="text-sm text-text-primary truncate" data-testid={`activity-actor-${log.id}`}>{log.actor_username ?? '—'}</span>
                <span className="text-xs text-text-secondary truncate" title={JSON.stringify(details)}>{detailParts.join(', ') || '—'}</span>
                <span className="text-xs text-mono text-text-secondary">{log.ip_addr ?? '—'}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main Entitlements Page ──────────────────────────────────────────

export default function AdminEntitlements() {
  const [activeTab, setActiveTab] = useState<TabId>('entitlements');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNanoModal, setShowNanoModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [activityFilters, setActivityFilters] = useState<AuditLogFilters>({});

  const entitlementState = useStore((state) => state.entitlement);
  const fetchEntitlements = useStore((state) => state.fetchEntitlements);
  const createEntitlement = useStore((state) => state.createEntitlement);
  const provisionNanoBundle = useStore((state) => state.provisionNanoBundle);
  const updateEntitlement = useStore((state) => state.updateEntitlement);
  const deleteEntitlement = useStore((state) => state.deleteEntitlement);
  const selectEntitlement = useStore((state) => state.selectEntitlement);
  const fetchProducts = useStore((state) => state.fetchProducts);
  const fetchEntitlementAgents = useStore((state) => state.fetchEntitlementAgents);
  const registerAgent = useStore((state) => state.registerAgent);
  const checkoutLicense = useStore((state) => state.checkoutLicense);
  const releaseLicense = useStore((state) => state.releaseLicense);
  const releaseAllLicenses = useStore((state) => state.releaseAllLicenses);
  const fetchEntitlementLeases = useStore((state) => state.fetchEntitlementLeases);
  const fetchEntitlementAlarms = useStore((state) => state.fetchEntitlementAlarms);
  const clearAlarm = useStore((state) => state.clearAlarm);
  const fetchAuditLog = useStore((state) => state.fetchAuditLog);
  const triggerCleanup = useStore((state) => state.triggerCleanup);

  const customers = useStore((state) => state.customer.customers);
  const fetchCustomers = useStore((state) => state.fetchCustomers);

  const { entitlements, products, agents, leases, alarms, auditLogs, selectedEntitlement, isLoading } = entitlementState;

  const fetchAll = useCallback(() => {
    fetchEntitlements();
    fetchProducts();
    fetchEntitlementAgents();
    fetchEntitlementLeases();
    fetchEntitlementAlarms();
  }, [fetchEntitlements, fetchProducts, fetchEntitlementAgents, fetchEntitlementLeases, fetchEntitlementAlarms]);

  useEffect(() => {
    fetchAll();
    fetchCustomers();
    const interval = setInterval(fetchAll, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAll, fetchCustomers]);

  // The activity log polls on its own so a filter change refetches only it.
  // Each poll is scheduled after the previous one settles, so a slow
  // response is never overlapped (or discarded) by the next tick.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      await fetchAuditLog(activityFilters);
      if (!cancelled) timer = setTimeout(tick, REFRESH_INTERVAL);
    };
    tick();
    return () => { cancelled = true; if (timer !== undefined) clearTimeout(timer); };
  }, [fetchAuditLog, activityFilters]);

  return (
    <div className="space-y-0 animate-fade-in" data-testid="admin-entitlements-page">
      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
      {activeTab === 'entitlements' && (
        <EntitlementsTab entitlements={entitlements} selectedEntitlement={selectedEntitlement} onSelect={selectEntitlement}
          onDelete={async (ent) => { await deleteEntitlement(ent.id); }}
          onUpdate={async (id, req) => { await updateEntitlement(id, req); }}
          onCreateClick={() => { fetchCustomers(); fetchProducts(); setShowCreateModal(true); }}
          onNanoBundleClick={() => { fetchCustomers(); setShowNanoModal(true); }} isLoading={isLoading}
        />
      )}
      {activeTab === 'agents' && (
        <AgentsTab agents={agents} products={products} leases={leases} onRegisterClick={() => setShowRegisterModal(true)}
          onCheckout={async (req, ip) => checkoutLicense(req, ip)}
          onRelease={async (leaseId) => { await releaseLicense([leaseId]); }}
          onReleaseAll={async (agentId) => { await releaseAllLicenses(agentId); }}
        />
      )}
      {activeTab === 'alarms' && <AlarmsTab alarms={alarms} onCleanup={async () => { await triggerCleanup(); }} onClear={async (id) => { await clearAlarm(id); }} />}
      {activeTab === 'activity' && (
        <ActivityLogTab logs={auditLogs} agents={agents} customers={customers}
          filters={activityFilters}
          onFilterChange={setActivityFilters}  /* the activity-log effect refetches on filter change */
        />
      )}

      <CreateEntitlementModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}
        onSubmit={async (req) => { await createEntitlement(req); }} customers={customers} products={products}
      />
      <NanoBundleModal isOpen={showNanoModal} onClose={() => setShowNanoModal(false)}
        onSubmit={async (req) => { await provisionNanoBundle(req); }} customers={customers}
      />
      <RegisterAgentModal isOpen={showRegisterModal} onClose={() => setShowRegisterModal(false)}
        onSubmit={async (req) => { await registerAgent(req); }}
      />
    </div>
  );
}
